import axios from 'axios';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useState } from 'react';
import { ToastType } from '@alefeducation/ui-elements-library-core';
import {
  Fields,
  START_UPLOADING_AVATARS,
  StartAvatarUpload,
  StartUploadAvatarResponse,
  StorageProvider,
} from '../../../apollo/queries/avatarUpload/startUploadAvatars.queries';
import {
  COMPLETE_AVATAR_UPLOAD,
  CompleteAvatarUploadInput,
  CompleteAvatarUploadResponse,
} from '../../../apollo/queries/avatarUpload/completeUploadAvatars.queries';
import appLocalStorage from '../../../utils/AppLocalStorage';
import { injectAutoHideToastOfType } from '../../../utils/Toast';
import { SOMETHING_WENT_WRONG_MESSAGE } from '../../../utils/constants';

export type UploadAvatarsProps = {
  files: File[];
  starCost: number;
  tenants: string[];
  assetCategory: string;
  genders: string[];
  organisation: string[];
  expiryDate: Date | null;
  isEnabledForAllOrgs: boolean;
};

type ReturnType = [(input: UploadAvatarsProps) => void, { loading: boolean }];

export const useStartUploadAvatarQuery = (onClose = () => {}): ReturnType => {
  const [loading, setLoading] = useState(false);
  const [startUploadAvatar] = useLazyQuery<StartUploadAvatarResponse>(
    START_UPLOADING_AVATARS,
  );

  const [completeUpload] = useMutation<
    CompleteAvatarUploadResponse,
    CompleteAvatarUploadInput
  >(COMPLETE_AVATAR_UPLOAD);

  const startUploadAvatarUrl = async (input: UploadAvatarsProps) => {
    setLoading(true);
    const filesArray = Array.from(input.files);
    try {
      const promises = filesArray.map(async (file) => {
        const variables = {
          avatarUploadInput: {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          },
        };
        const response = await startUploadAvatar({ variables });
        if (response.data) {
          await uploadAvatar(response.data, file);
          const completeUploadVariables: CompleteAvatarUploadInput = {
            uploadId: response.data.startAvatarUpload.uploadId,
            avatarInput: {
              name: file.name,
              description: 'some description',
              validTill: input.expiryDate,
              organizations: input.organisation,
              isEnabledForAllOrgs: input.isEnabledForAllOrgs,
              tenants: input.tenants,
              createdBy: appLocalStorage.getAdminUUID()!,
              starCost: String(input.starCost),
              genders: input.genders,
              category: input.assetCategory,
            },
          };
          return await completeUpload({
            variables: completeUploadVariables,
          });
        } else {
          return Promise.resolve({
            data: { completePredefinedAvatarUpload: { status: 'FAILED' } },
          });
        }
      });
      const response = await Promise.all(promises);
      const hasFailedRes = response.find(
        (res) =>
          res?.data?.completePredefinedAvatarUpload.status !== 'SUCCESSFUL',
      );

      if (hasFailedRes) {
        const desc =
          filesArray.length === 1
            ? `We could not upload ${filesArray[0].name} right now.`
            : 'We could not upload some of the avatars right now.';
        errorToast(desc);
      } else {
        const desc =
          filesArray.length === 1
            ? `You have successfully uploaded ${filesArray[0].name}.`
            : 'You have successfully uploaded avatars.';
        injectAutoHideToastOfType(
          'avatar-upload-toast',
          'Successfully Uploaded',
          ToastType.SUCCESS,
          desc,
        );
      }
    } catch (error) {
      const desc =
        filesArray.length === 1
          ? `We could not upload ${filesArray[0].name} right now.`
          : 'We could not upload avatars right now.';
      errorToast(desc);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const errorToast = (des: string) =>
    injectAutoHideToastOfType(
      'avatar-upload-toast',
      SOMETHING_WENT_WRONG_MESSAGE,
      ToastType.ERROR,
      des,
    );

  return [startUploadAvatarUrl, { loading }];
};

export function uploadAvatarRequest(response: StartAvatarUpload, file: File) {
  const formData = new FormData();
  response.presignedUrl.fields.forEach((field: Fields) => {
    formData.append(field.key, field.value);
  });
  formData.append('file', file);
  return formData;
}

export const uploadAvatar = async (
  startUploadResponse: StartUploadAvatarResponse,
  file: File,
) => {
  const { url } = startUploadResponse.startAvatarUpload.presignedUrl;
  const request = uploadAvatarRequest(
    startUploadResponse.startAvatarUpload,
    file,
  );

  if (
    startUploadResponse.startAvatarUpload.storageProvider ===
    StorageProvider.AZURE_BLOB
  ) {
    await axios.put(url, file, {
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': 'multipart/form-data',
      },
    });
  } else {
    await axios.post(url, request, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
};
